import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
      } else {
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      }
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // Check if sections are available
        if (parsedResponseText.sections && parsedResponseText.sections[this.sectionId]) {
          // Use the normal section update flow
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } else {
          // Fallback to targeted manual updates when sections are null
          console.log('Sections not available, using targeted manual update');
          this.updateCartTargeted(parsedResponseText);
          
          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: parsedResponseText.item_count,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the cart with targeted changes
   * @param {Object} cartData - The cart data from AJAX response
   */
  updateCartTargeted(cartData) {
    console.log('Updating cart manually with:', cartData);
    
    // Always update cart count first
    this.updateCartCountTargeted(cartData.item_count);
    
    // Always update totals
    this.updateCartTotalsTargeted(cartData);
    
    // Handle cart items based on item count
    if (cartData.item_count === 0) {
      console.log('Cart is empty, showing empty state');
      this.handleEmptyCartImmediately();
    } else {
      console.log('Cart has items, updating quantities');
      this.updateCartItemsTargeted(cartData.items);
    }
  }

  /**
   * Updates cart count targeting specific safe elements
   * @param {number} itemCount - The new item count
   */
  updateCartCountTargeted(itemCount) {
    try {
      console.log('Updating cart count to:', itemCount);
      
      // Update cart drawer count specifically
      const cartDrawerCount = this.querySelector('[ref="cartItemCount"]');
      if (cartDrawerCount) {
        cartDrawerCount.textContent = itemCount;
      }

      // Update header cart count if it exists and is simple
      const headerCartCount = document.querySelector('header [ref="cartItemCount"], header .cart-count');
      if (headerCartCount && this.isSimpleTextElement(headerCartCount)) {
        headerCartCount.textContent = itemCount;
      }

      // Update cart bubble in header if it exists
      const cartBubble = document.querySelector('.cart-bubble, .cart-count-bubble, [data-cart-bubble]');
      if (cartBubble) {
        if (itemCount > 0) {
          cartBubble.textContent = itemCount;
          cartBubble.style.display = 'flex';
        } else {
          cartBubble.style.display = 'none';
        }
      }
    } catch (error) {
      console.warn('Error updating cart count:', error);
    }
  }

  /**
   * Updates cart totals targeting cart drawer specifically
   * @param {Object} cartData - The complete cart data
   */
  updateCartTotalsTargeted(cartData) {
    try {
      console.log('Updating cart totals - Subtotal:', cartData.items_subtotal_price, 'Total:', cartData.total_price);
      
      // Update subtotal in cart drawer
      const subtotalElement = this.querySelector('.cart-subtotal, [data-cart-subtotal]');
      if (subtotalElement) {
        subtotalElement.textContent = this.formatPrice(cartData.items_subtotal_price);
      }
      
      // Update total in cart drawer
      const totalElement = this.querySelector('.cart-total, [data-cart-total]');
      if (totalElement) {
        totalElement.textContent = this.formatPrice(cartData.total_price);
      }

      // Update any item count in cart drawer
      const itemCountElement = this.querySelector('.cart-item-count, [data-item-count]');
      if (itemCountElement) {
        itemCountElement.textContent = cartData.item_count;
      }
    } catch (error) {
      console.warn('Error updating cart totals:', error);
    }
  }

  /**
   * Updates cart items with quantity changes
   * @param {Array} items - The cart items
   */
  updateCartItemsTargeted(items) {
    try {
      console.log('Updating cart items:', items);
      
      // Update quantities for existing items without replacing elements
      items.forEach(item => {
        const itemElement = this.querySelector(`[data-variant-id="${item.variant_id}"]`);
        if (itemElement) {
          this.updateCartItemQuantity(itemElement, item.quantity);
        } else {
          console.log('Item element not found for variant:', item.variant_id);
        }
      });

      // Remove items that are no longer in the cart
      this.removeMissingItems(items);
    } catch (error) {
      console.warn('Error updating cart items:', error);
    }
  }

  /**
   * Removes items that are no longer in the cart
   * @param {Array} currentItems - The current items in cart
   */
  removeMissingItems(currentItems) {
    try {
      const cartItemsContainer = this.querySelector('.cart-items, [data-cart-items]');
      if (!cartItemsContainer) return;

      const currentVariantIds = currentItems.map(item => item.variant_id.toString());
      const existingItems = cartItemsContainer.querySelectorAll('[data-variant-id]');
      
      existingItems.forEach(item => {
        const variantId = item.getAttribute('data-variant-id');
        if (!currentVariantIds.includes(variantId)) {
          console.log('Removing item with variant:', variantId);
          this.removeCartItemWithAnimation(item);
        }
      });
    } catch (error) {
      console.warn('Error removing missing items:', error);
    }
  }

  /**
   * Removes a cart item with animation
   * @param {HTMLElement} item - The cart item to remove
   */
  removeCartItemWithAnimation(item) {
    const removeItem = () => {
      item.remove();
      // Check if cart is now empty after removal
      this.checkIfCartIsEmpty();
    };

    if (prefersReducedMotion()) {
      removeItem();
    } else {
      item.style.setProperty('--row-height', `${item.clientHeight}px`);
      item.classList.add('removing');
      onAnimationEnd(item, removeItem);
    }
  }

  /**
   * Checks if cart is empty and shows empty state if needed
   */
  checkIfCartIsEmpty() {
    const cartItemsContainer = this.querySelector('.cart-items, [data-cart-items]');
    if (!cartItemsContainer) return;

    const remainingItems = cartItemsContainer.querySelectorAll('[data-variant-id]');
    if (remainingItems.length === 0) {
      console.log('Cart is now empty, showing empty state');
      this.showEmptyCartMessage(cartItemsContainer);
    }
  }

  /**
   * Handles empty cart state immediately (no animation)
   */
  handleEmptyCartImmediately() {
    try {
      console.log('Handling empty cart immediately');
      const cartItemsContainer = this.querySelector('.cart-items, [data-cart-items]');
      
      if (!cartItemsContainer) return;

      // Remove all cart items immediately without animation
      const cartItems = cartItemsContainer.querySelectorAll('[data-variant-id]');
      cartItems.forEach(item => {
        item.remove();
      });

      // Show empty cart message immediately
      this.showEmptyCartMessage(cartItemsContainer);
    } catch (error) {
      console.warn('Error handling empty cart immediately:', error);
    }
  }

  /**
   * Updates quantity for a single cart item
   * @param {HTMLElement} itemElement - The cart item element
   * @param {number} quantity - The new quantity
   */
  updateCartItemQuantity(itemElement, quantity) {
    // Update quantity display
    const quantityDisplay = itemElement.querySelector('.quantity, [data-quantity]');
    if (quantityDisplay) {
      quantityDisplay.textContent = quantity;
    }

    // Update quantity input if exists
    const quantityInput = itemElement.querySelector('input[type="number"]');
    if (quantityInput) {
      quantityInput.value = quantity;
    }

    // Update line price if exists
    const priceElement = itemElement.querySelector('.cart-item-price');
    const linePriceElement = itemElement.querySelector('.line-price, [data-line-price]');
    if (priceElement && linePriceElement) {
      const price = this.extractPrice(priceElement.textContent);
      if (price) {
        linePriceElement.textContent = this.formatPrice(price * quantity);
      }
    }
  }

  /**
   * Extracts price from formatted string
   * @param {string} priceText - Formatted price string
   * @returns {number|null} Price in cents or null
   */
  extractPrice(priceText) {
    try {
      // Remove currency symbols and commas, then parse
      const numericString = priceText.replace(/[^\d.,]/g, '').replace(',', '');
      const price = parseFloat(numericString);
      return isNaN(price) ? null : Math.round(price * 100);
    } catch (error) {
      return null;
    }
  }

  /**
   * Shows empty cart message
   * @param {HTMLElement} container - The container to show the message in
   */
  showEmptyCartMessage(container) {
    try {
      console.log('Showing empty cart message');
      container.innerHTML = this.getEmptyCartHTML();
      const cartSummary = document.querySelector('.cart-drawer__summary');
    
      if (cartSummary) {
          cartSummary.style.display = 'none';
      }
    } catch (error) {
      console.warn('Error showing empty cart message:', error);
    }
  }

  /**
   * Checks if an element is safe to update (not a complex component)
   * @param {HTMLElement} element - The element to check
   * @returns {boolean} True if safe to update
   */
  isSimpleTextElement(element) {
    // Only avoid updating elements that are direct children of problematic components
    const problematicComponents = ['CART-ICON', 'PRODUCT-FORM-COMPONENT', 'PRODUCT-CARD', 'SLIDESHOW-COMPONENT'];
    
    // Check if element is directly inside a problematic component
    const parent = element.parentElement;
    if (parent && problematicComponents.includes(parent.tagName)) {
      return false;
    }
    
    return true;
  }

  /**
   * Returns HTML for empty cart state
   * @returns {string} HTML string
   */
  getEmptyCartHTML() {
    return `
      
      <div class="custom-empty-cart cart-drawer__content motion-reduce" aria-label="Cart">

             
            
              <span class="h2 drawer_summary_title cart-drawer__heading">
                Cart Summary
              </span>
            

            <span class="cart-drawer__heading h3 cart-drawer__heading--empty">
              Your cart is empty
            </span>

            <div class="cart-drawer__items">
              

            <script src="//kortons.com/cdn/shop/t/114/assets/component-cart-items.js?v=42435336372673957621763928286" type="module" fetchpriority="low"></script>

            <div class="cart-items__wrapper">
              
            <p>
                    Have an account? <a href="https://account.kortons.com?locale=en&amp;region_country=IN">Log in</a> to check out faster.
                  </p><a class="button cart-items__empty-button" href="/collections/more">
                  Continue Shopping
                </a></div>



                        </div>

                        
                        
                          <span class="h5 drawer_shop_name">
                            KORTONS BRAND EYELET COMPANY INC.
                          </span>
                        
                      </div>
    `;
  }

  /**
   * Formats price for display
   * @param {number} price - Price in cents
   * @returns {string} Formatted price
   */
  formatPrice(price) {
    // Format price based on your currency needs
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price / 100);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
